import * as React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CommonModal } from '../../../../../global/modal/common/common-modal';
import { DefaultCloseModalButton } from '../../../../../global/modal/common/common-close-modal-buttons';
import { FormField } from '../../../../../global/form/form-field/form-field';
import DefaultButton from '../../../../../global/default-button/default-button';
import { ZoomMeetingVM } from './zoom-meeting.viewmodel';
import './zoom-meeting.scss';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import { OERadioOptions } from '../../../../../global/oe-radio-options/oe-radio-options';
import { ZoomMeetingType } from '../../../../../../api/zoom.dao';
import { RecurringZoomForm } from './recurring-zoom-meeting-form/recurring-zoom-form.presentation';
import { ScheduledMeetingForm } from './scheduled-meeting-form';
import { LOADING } from '../../../../../../constants/common-labels.constants';

interface IPresentationProps {
    vm: ZoomMeetingVM;
    isMeetingConnectionsForm?: boolean;
    isAutoSyncLimitReached?: boolean;
}

const ZoomMeetingModal = mobxReact.observer(({
    vm,
    isMeetingConnectionsForm,
    isAutoSyncLimitReached
}: IPresentationProps) => {
    const formVM = vm;
    const formData = formVM.createZoomMeetingFormDataVM;
    const formRecurringMeetingData = formVM.recurringZoomMeetingFormDataVM;
    const isEditMode = formVM.getEditModeStatus();
    const label = isEditMode ? 'Edit Details Manually' : 'Add Details Manually';
    const title = isEditMode ? 'Edit Zoom Meeting' : 'Create Zoom Meeting';
    const defSubmit = isEditMode ? formVM.updateZoomMeetingHandle : formVM.createZoomMeetingHandle;
    const defLabel = isEditMode ? 'Edit Zoom Meeting' : 'Create Zoom Meeting';
    const { formatMessage } = useIntl();

    const showAutoRegAndAutoSyncToggles = [
        isMeetingConnectionsForm,
        !isAutoSyncLimitReached,
        hasPermission(Permission.CREATE_AUTOSYNC_CONNECTION)
    ].every(Boolean);

    const handleRequestClose = () => {
        formVM.hideForm();
        formVM.setDisableEditMode();
    };

    const footer = <div>
        {formVM.zoomMeetingType === ZoomMeetingType.SCHEDULED_MEETING ?
            <div className='buttons-block'>
                <DefaultButton
                    text={label}
                    modifier={['fat', 'cancel']}
                    onClick={isEditMode
                        ? formVM.updateZoomMeetingManuallyHandle
                        : () => formVM.createZoomMeetingManuallyHandle(showAutoRegAndAutoSyncToggles)} />
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultButton
                        text={defLabel}
                        onClick={defSubmit}
                        modifier={['fat', 'confirm-safe']}
                        disabled={formVM.getIsFormSaving()} />
                    <DefaultCloseModalButton />
                </div>
            </div>
            : <div className='buttons-block__recurring-meeting'>
                <DefaultCloseModalButton />
                <DefaultButton
                    text={defLabel}
                    modifier={['fat', 'confirm-safe']}
                    onClick={() => formVM.createRecurringZoomMeetingHandle()}
                    disabled={formVM.getIsRecurringMeetingFormSaving()}
                />
            </div>
        }
    </div>;

    return (
        <>
            <CommonModal
                title={title}
                isOpen={formVM.getIsFormShown()}
                onRequestClose={handleRequestClose}
                className='oe-modal-width create-zoom-meeting-modal'
                classNameBody='body__scrollable'
                footer={footer}
            >
                <form className='oe-form'>
                    {(vm.getRecurringZoomMeetingFormShownState() && vm.isLoadingAccountAdmin)
                        ? <FormattedMessage
                            {...LOADING}
                            values={{
                                suffix: '...'
                            }}
                        />
                        : <>
                            {vm.getRecurringZoomMeetingsEnabledAndFormShown()
                                && <FormField className='m-bottom-25px'>
                                    <OERadioOptions
                                        options={[
                                            {
                                                value: ZoomMeetingType.SCHEDULED_MEETING,
                                                label: formatMessage({
                                                    id: 'oe.zoomWebinar.scheduledMeeting',
                                                    description: 'Scheduled Meeting label',
                                                    defaultMessage: 'Scheduled Meeting'
                                                }),
                                            },
                                            {
                                                value: ZoomMeetingType.RECURRING_NO_FIXED_MEETING,
                                                label: formatMessage({
                                                    id: 'oe.zoomWebinar.recurringMeeting',
                                                    description: 'Recurring Meeting label',
                                                    defaultMessage: 'Recurring Meeting; No fixed time'
                                                }),
                                            }
                                        ]}
                                        value={vm.zoomMeetingType}
                                        onOptionToggle={(value: ZoomMeetingType) => vm.onChangeZoomMeetingType(value)}
                                        styles='website-custom__radio column'
                                    />
                                </FormField>}

                            {vm.zoomMeetingType === ZoomMeetingType.SCHEDULED_MEETING
                                ? <ScheduledMeetingForm
                                    isEditMode={isEditMode}
                                    formVM={formVM}
                                    formData={formData}
                                    showAutoRegAndAutoSyncToggles={showAutoRegAndAutoSyncToggles}
                                />
                                : <RecurringZoomForm formRecurringMeetingData={formRecurringMeetingData} />
                            }

                        </>
                    }
                </form>
            </CommonModal>
        </>
    );
});

export { ZoomMeetingModal };
